import { envelopeField, type MaeizEnvelope, type User } from "@maeiz/core";
import { httpClient } from "@api/http";

async function post(url: string, body?: unknown): Promise<MaeizEnvelope> {
    return (await httpClient.post<unknown>(url, body)) as unknown as MaeizEnvelope;
}

async function get(url: string): Promise<MaeizEnvelope> {
    return (await httpClient.get<unknown>(url)) as unknown as MaeizEnvelope;
}

export interface LoginPayload {
    email: string;
    password: string;
}

export interface LoginResult {
    user: User;
    token: string;
}

export interface MeResult {
    user: User;
    role: string;
    permissions: string[];
}

export interface ResetPasswordPayload {
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
}

export const authApi = {
    async login(data: LoginPayload): Promise<LoginResult> {
        const res = await post("/auth/login", data);
        const token =
            envelopeField<string>(res, "accessToken") || envelopeField<string>(res, "token");
        const user = envelopeField<User>(res, "user");
        if (!token || !user) {
            throw new Error("Unexpected login response from server.");
        }
        return { user, token };
    },

    async me(): Promise<MeResult> {
        const res = await get("/auth/me");
        return {
            user: envelopeField<User>(res, "user"),
            role: envelopeField<string>(res, "role", ""),
            permissions: envelopeField<string[]>(res, "permissions", []),
        };
    },

    async logout(): Promise<void> {
        await post("/auth/logout");
    },

    async forgotPassword(email: string): Promise<void> {
        await post("/auth/forgot-password", { email });
    },

    async verifyOtp(email: string, otp: string): Promise<boolean> {
        const res = await post("/auth/verify-otp", { email, otp });
        return envelopeField<boolean>(res, "verified", false) === true;
    },

    async resetPassword(data: ResetPasswordPayload): Promise<void> {
        await post("/auth/reset-password", {
            email: data.email,
            otp: data.otp,
            password: data.password,
            confirm_password: data.confirmPassword,
        });
    },

    async refreshToken(): Promise<{ token: string; user?: User } | null> {
        const res = await post("/auth/refresh-token");
        const token = envelopeField<string>(res, "accessToken", "");
        if (!token) {
            return null;
        }
        return { token, user: envelopeField<User>(res, "user") };
    },
};
